import { env } from "@/app/env";

export type GuestDownloadStatus = "PENDING" | "APPROVED" | "EXPIRED" | "REVOKED";

export type GuestDownloadArtifactRecord = {
  releaseId: string;
  appId: string;
  version: string;
  channel: string;
  title: string;
  publishedAt?: string;
  artifactId: string;
  os: string;
  arch: string;
  kind: "archive" | "installer";
  filename: string;
  contentType: string;
  sizeBytes: number;
  sha256: string;
  defaultDownload: boolean;
  signingState: "not_applicable" | "unsigned" | "signed" | "notarized" | "stapled";
  webview2Strategy?: "download" | "embed" | "browser" | "error";
  downloadUrl: string;
};

export type GuestDownloadRequestRecord = {
  requestId: string;
  requestCode: string;
  requestSecret?: string;
  status: GuestDownloadStatus;
  expiresAt: string;
  pollUrl?: string;
  downloads?: GuestDownloadArtifactRecord[];
};

export function createGuestDownloadRequest() {
  return publicRequest<GuestDownloadRequestRecord>("/download/v1/guest-requests", {
    method: "POST"
  });
}

export function getGuestDownloadRequestStatus(requestId: string, requestSecret: string) {
  return publicRequest<GuestDownloadRequestRecord>(
    `/download/v1/guest-requests/${encodeURIComponent(requestId)}/status?secret=${encodeURIComponent(requestSecret)}`
  );
}

export function guestDownloadHref(downloadUrl: string, requestSecret: string) {
  const separator = downloadUrl.includes("?") ? "&" : "?";
  return `${env.apiBaseUrl}${downloadUrl}${separator}secret=${encodeURIComponent(requestSecret)}`;
}

async function publicRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${env.apiBaseUrl}${path}`, {
    ...init,
    headers: {
      Accept: "application/json",
      ...init.headers
    }
  });
  if (!response.ok) {
    const message = await publicErrorMessage(response);
    throw new Error(message);
  }
  return (await response.json()) as T;
}

async function publicErrorMessage(response: Response) {
  try {
    const body = (await response.json()) as { error?: { message?: string; code?: string } };
    return body.error?.message || body.error?.code || response.statusText;
  } catch {
    return response.statusText;
  }
}
